import { useStore } from 'zustand';
import { useCallback } from 'react';

import { config } from '@/config/config';
import { buildUrlQueryParams } from '@/utils/url.util';
import { SearchType } from '@/types/most-search-type';
import { SelectedProcedures } from '@/app/(main)/(pages)/_components/search/search.component';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { userLocationStore } from '@/store/user-location.store';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';
import { isProcedureSearch } from '@/app/_utils/is-procedure-search.util';

interface UseSearchProps {
  locationOverride?: OverridedUserLocation;
}

export function useSearch({ locationOverride }: UseSearchProps = {}) {
  const enabledRedirect = config.stageOneEnabled;
  const userStore = useStore(userLocationStore, (state) => state.userLocation);

  const searchParams = useSearchParams(Object.values(PersistedUserNavigationSearchParams));
  const cityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);

  const userLocation = locationOverride ?? userStore;

  const resolveName = (searchResult: SearchResult): string => {
    if (
      searchResult.searchType === SearchType.DOCTOR
      || searchResult.searchType === SearchType.CLINIC
      || searchResult.searchType === SearchType.BRANCH_LABORATORY
    ) {
      return formatToTitleCase(`${searchResult.displayName}`);
    }
    return `${searchResult.displayName}`;
  };

  const buildExamsUrl = (searchResult: SearchResult): string => {
    const basePath = searchResult.searchType === SearchType.VACCINE ? '/busca/vacina/' : '/busca/exames-laboratoriais/';

    const procedureCodes = searchResult?.code?.split(',').filter(Boolean);
    return buildUrlQueryParams(basePath, {
      exames: procedureCodes || [''],
    });
  };

  const resolveLink = useCallback((searchResult: SearchResult, otherParams: Record<string, string> = {}): string => {
    if (!searchResult.slug && !searchResult.code) {
      return '/';
    }

    const linkMap = {
      [SearchType.DOCTOR]: `/profissionais/${searchResult.slug}/`,
      [SearchType.CLINIC]: `/parceiros/${searchResult.slug}/`,
      [SearchType.BRANCH_LABORATORY]: `/laboratorios/${searchResult.slug}/`,
      [SearchType.LABORATORIAL_EXAM]: buildExamsUrl(searchResult),
      [SearchType.VACCINE]: buildExamsUrl(searchResult),
    };

    const slug = searchResult.slug || searchResult.code;
    const pathUrl = linkMap[searchResult.searchType as keyof typeof linkMap] || `/busca/${slug}/`;

    const shouldRedirect = enabledRedirect && isProcedureSearch(searchResult.searchType as SearchType);

    const city: string | undefined = cityParam ?? userLocation?.city ?? searchResult?.city;
    const overridesCityParams = city ? { [PersistedUserNavigationSearchParams.CIDADE]: city } : undefined;

    const persistLocationJson = userLocation?.formattedAddress && shouldRedirect
      ? {
          [PersistedUserNavigationSearchParams.LOCATION]: JSON.stringify(userLocation),
        }
      : undefined;

    const fullUrl = shouldRedirect
      ? buildUrlQueryParams(`${config.ag.url}${pathUrl}`, otherParams)
      : pathUrl;

    return buildUrlWithPersistedParams({
      baseUrl: fullUrl,
      overrides: { ...overridesCityParams, ...persistLocationJson },
    });
  }, [cityParam, enabledRedirect, userLocation]);

  const handleSearchExamsUrl = useCallback((isVaccine: boolean, selectedProcedures: SelectedProcedures[]): string => {
    const basePath = isVaccine ? '/busca/vacina/' : '/busca/exames-laboratoriais/';
    const pathUrl = buildUrlQueryParams(basePath, {
      exames: selectedProcedures.map((exam) => exam.code),
    });

    const city: string | undefined = userLocation?.city ?? (cityParam ?? undefined);
    const persistedCityParams = city
      ? {
          [PersistedUserNavigationSearchParams.CIDADE]: city,
        }
      : undefined;

    const persistLocationJson = userLocation?.formattedAddress && enabledRedirect
      ? {
          [PersistedUserNavigationSearchParams.LOCATION]: JSON.stringify(userLocation),
        }
      : undefined;

    const baseUrl = enabledRedirect ? `${config.ag.url}${pathUrl}` : pathUrl;

    return buildUrlWithPersistedParams({
      baseUrl,
      overrides: { ...persistedCityParams, ...persistLocationJson },
    });
  }, [cityParam, enabledRedirect, userLocation]);

  return {
    isProcedureSearch,
    resolveName,
    resolveLink,
    buildExamsUrl,
    handleSearchExamsUrl,
  };
}
